import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserListings, deleteListing } from '../services/listingService'; // Import deleteListing
import { getUserDoc } from '../services/userService';

const Dashboard = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const [deletingId, setDeletingId] = useState(null); // Track ID of listing being deleted
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // Control modal visibility
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const userListings = await getUserListings(currentUser.uid);
        setListings(userListings);
      } catch (error) {
        console.error('Error fetching user listings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [currentUser]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userData = await getUserDoc(currentUser.uid);
        setUserProfile(userData);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    if (currentUser) {
      fetchUserProfile();
    }
  }, [currentUser]);

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteListing(deletingId);
      // Refresh listings after successful deletion
      const userListings = await getUserListings(currentUser.uid);
      setListings(userListings);
    } catch (error) {
      console.error('Error deleting listing:', error);
      // Handle error (e.g., display an error message)
    } finally {
      setShowDeleteConfirmation(false);
      setDeletingId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDeletingId(null);
  };

  const truncateTitle = (title, maxLength) => {
    if (!title) return '';
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Add Profile Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          Profile Information
          <span className="text-sm font-normal text-gray-500 flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Visible only to you
          </span>
        </h2>
        {userProfile ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-medium">{userProfile.firstName || 'N/A'} {userProfile.lastName || ''}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{userProfile.email}</p>
            </div>
          </div>
        ) : (
          <div className="text-gray-500">Loading profile information...</div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md">
            <h3 className="text-xl font-bold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this listing? This action cannot be undone.
            </p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">My Listings</h1>
        <Link
          to="/edit-listing"
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Add New Site
        </Link>
      </div>

      {listings.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg">
          <p className="text-gray-600 mb-4">You haven't added any sites yet.</p>
          <Link
            to="/edit-listing"
            className="text-red-600 hover:underline"
          >
            Add your first site
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {listings.map(listing => (
            <div
              key={listing.id}
              className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between">
                <div className="flex-1 overflow-hidden mr-4">
                  <h2 className="text-xl font-semibold mb-2" title={listing.name}>{truncateTitle(listing.name, 70)}</h2>
                  <p className="text-gray-600 mb-2">{listing.description}</p>
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-gray-500">Category: {listing.category}</p>
                    <span className={`text-sm px-2 py-1 rounded ${
                      listing.isPublished 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {listing.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 flex flex-col justify-center space-y-2"> {/* Modified this container */}
                  <Link
                    to={`/edit-listing/${listing.id}`}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDeleteClick(listing.id)}
                    className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 text-center">
        <Link
          to="/"
          className="text-red-600 hover:underline"
        >
          View all listings in directory
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
